import type {
  BusinessProductionSummary,
  CandidateGroup,
  CandidateTab,
  LinkConfidence,
  ProductionClassificationValue,
  ProductionConnectionStatus,
  ProductionEntityRef,
} from '@quds/shared';
import { apiGet, apiPost } from '@/lib/api';
import { invalidate } from '../invalidation';

/** Production linking API — Central Admin only; never calls quds-pos-cloud directly. */

export type ProductionStatusResponse = ProductionConnectionStatus & {
  unavailable?: boolean;
};

export interface ProductionCandidatesQuery {
  tab?: CandidateTab;
  q?: string;
  confidence?: LinkConfidence;
  classification?: ProductionClassificationValue;
}

export interface ProductionCandidatesResult {
  items: CandidateGroup[];
  total: number;
  productionStatus: ProductionStatusResponse | Record<string, unknown>;
  unavailable?: boolean;
}

export interface ConfirmLinksInput {
  groupKey: string;
  createBusiness?: {
    name?: string;
    category?: 'PHARMACY' | 'SUPERMARKET' | 'RESTAURANT' | 'OTHER';
    country?: string;
    city?: string;
    contactPerson?: string;
    phone?: string;
  };
  existingBusinessId?: string;
  selected: Array<{
    ref: ProductionEntityRef;
    role?: 'primary' | 'historical' | 'note';
    validFrom?: string | null;
    validTo?: string | null;
    branchId?: string | null;
    lowReason?: string;
    expectedConfidence?: LinkConfidence;
  }>;
  asProposed?: boolean;
}

export interface ConfirmLinksResult {
  businessId: string;
  linkIds: string[];
  createdBusiness: boolean;
}

export const getProductionStatus = () => apiGet<ProductionStatusResponse>('/production/status');

export const listProductionCandidates = (q: ProductionCandidatesQuery = {}) =>
  apiGet<ProductionCandidatesResult>('/production/candidates', {
    tab: q.tab,
    q: q.q?.trim(),
    confidence: q.confidence,
    classification: q.classification,
  });

export const getProductionCandidate = (groupKey: string) =>
  apiGet<CandidateGroup>(`/production/candidates/${encodeURIComponent(groupKey)}`);

export async function classifyProductionRecord(input: {
  ref: ProductionEntityRef;
  classification: ProductionClassificationValue;
  reason: string;
}): Promise<{ id: string; created: boolean }> {
  const result = await apiPost<{ id: string; created: boolean }>('/production/classify', input);
  invalidate();
  return result;
}

export async function bootstrapProductionClassifications(): Promise<{
  applied: number;
  suggestions: unknown[];
}> {
  const result = await apiPost<{ applied: number; suggestions: unknown[] }>('/production/bootstrap-classifications');
  invalidate();
  return result;
}

export async function confirmProductionLinks(input: ConfirmLinksInput): Promise<ConfirmLinksResult> {
  const result = await apiPost<ConfirmLinksResult>('/production/confirm', input);
  invalidate();
  return result;
}

export async function rejectProductionLinks(input: {
  businessId: string;
  refs: ProductionEntityRef[];
  reason?: string;
}): Promise<{ ids: string[] }> {
  const result = await apiPost<{ ids: string[] }>('/production/reject', input);
  invalidate();
  return result;
}

export async function retireProductionLink(input: { linkId: string; reason: string }): Promise<{ id: string }> {
  const result = await apiPost<{ id: string }>('/production/retire', input);
  invalidate();
  return result;
}

export async function leaveProductionUnresolved(): Promise<{ ok: true }> {
  return apiPost<{ ok: true }>('/production/leave-unresolved');
}

export const getBusinessProductionSummary = (businessId: string) =>
  apiGet<BusinessProductionSummary>(`/production/businesses/${encodeURIComponent(businessId)}/summary`);
